"use client";

import { c as _c } from "react/compiler-runtime";
import type { CreateDropPart, ReferencedNft } from "@/entities/IDrop";
import type { ApiDropMentionedUser } from "@/generated/models/ApiDropMentionedUser";
import { AnimatePresence, motion } from "framer-motion";
import Link from "next/link";
import type { FC } from "react";
import { memo, useContext, useEffect, useMemo, useRef, useState } from "react";
import { AuthContext } from "../auth/Auth";
import UserCICAndLevel, { UserCICAndLevelSize } from "../user/utils/UserCICAndLevel";
import CreateDropStormPart from "./CreateDropStormPart";
interface CreateDropStormPartsProps {
  parts: CreateDropPart[];
  mentionedUsers: ApiDropMentionedUser[];
  referencedNfts: ReferencedNft[];
  onRemovePart: (partIndex: number) => void;
}
const CreateDropStormParts: FC<CreateDropStormPartsProps> = t0 => {
  const $ = _c(38);
  const {
    parts,
    mentionedUsers,
    referencedNfts,
    onRemovePart
  } = t0;
  const {
    connectedProfile
  } = useContext(AuthContext);
  const partIdCounterRef = useRef(0);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = new Map();
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [partIdsMap, setPartIdsMap] = useState(t1);
  let t2;
  let t3;
  if ($[1] !== parts) {
    t2 = () => {
      setPartIdsMap(prevMap => {
        const newMap = new Map(prevMap);
        let changed = false;
        parts.forEach((part, index) => {
          if (!part.quoted_drop) {
            if (!newMap.has(index)) {
              partIdCounterRef.current = partIdCounterRef.current + 1;
              newMap.set(index, `part-${partIdCounterRef.current}`);
              changed = true;
            }
          }
        });
        const maxIndex = parts.length - 1;
        Array.from(newMap.keys()).forEach(key => {
          if (key > maxIndex) {
            newMap.delete(key);
            changed = true;
          }
        });
        return changed ? newMap : prevMap;
      });
    };
    t3 = [parts];
    $[1] = parts;
    $[2] = t2;
    $[3] = t3;
  } else {
    t2 = $[2];
    t3 = $[3];
  }
  useEffect(t2, t3);
  let t4;
  if ($[4] !== partIdsMap || $[5] !== parts) {
    let t5;
    if ($[7] !== partIdsMap) {
      t5 = (part_0, index_0) => {
        if (part_0.quoted_drop) {
          return `quoted-${part_0.quoted_drop.drop_id}-${part_0.quoted_drop.drop_part_id}`;
        }
        return partIdsMap.get(index_0) ?? `part-fallback-${index_0}`;
      };
      $[7] = partIdsMap;
      $[8] = t5;
    } else {
      t5 = $[8];
    }
    t4 = parts.map(t5);
    $[4] = partIdsMap;
    $[5] = parts;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const partKeys = t4;
  let t5;
  if ($[9] !== connectedProfile) {
    t5 = <div className="tw-h-10 tw-w-10 tw-bg-iron-900 tw-relative tw-flex-shrink-0 tw-rounded-lg">{connectedProfile?.pfp ? <img src={connectedProfile.pfp} alt={connectedProfile.handle ?? "user"} className="tw-h-full tw-w-full tw-object-cover tw-rounded-lg" /> : <div className="tw-h-full tw-w-full tw-rounded-lg tw-bg-iron-900" />}</div>;
    $[9] = connectedProfile;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const t6 = connectedProfile?.level ?? 0;
  let t7;
  if ($[11] !== t6) {
    t7 = <UserCICAndLevel level={t6} size={UserCICAndLevelSize.SMALL} />;
    $[11] = t6;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  const t8 = `/${connectedProfile?.handle}`;
  const t9 = connectedProfile?.handle ?? "user";
  let t10;
  if ($[13] !== t8 || $[14] !== t9) {
    t10 = <p className="tw-text-md tw-mb-0 tw-leading-none tw-font-semibold"><Link href={t8} className="tw-no-underline tw-text-iron-200 hover:tw-text-iron-500 tw-transition tw-duration-300 tw-ease-out">{t9}</Link></p>;
    $[13] = t8;
    $[14] = t9;
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  let t11;
  if ($[16] !== t10 || $[17] !== t7) {
    t11 = <div className="tw-flex tw-items-center tw-gap-x-2"><div className="tw-flex tw-items-center tw-gap-x-2">{t7}{t10}</div></div>;
    $[16] = t10;
    $[17] = t7;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  let t12;
  if ($[19] !== mentionedUsers || $[20] !== onRemovePart || $[21] !== partKeys || $[22] !== parts || $[23] !== referencedNfts) {
    let t13;
    if ($[25] !== mentionedUsers || $[26] !== onRemovePart || $[27] !== partKeys || $[28] !== referencedNfts) {
      t13 = (part_1, partIndex) => <motion.div key={partKeys[partIndex]} initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.3
      }}><CreateDropStormPart partIndex={partIndex} part={part_1} mentionedUsers={mentionedUsers} referencedNfts={referencedNfts} onRemovePart={onRemovePart} /></motion.div>;
      $[25] = mentionedUsers;
      $[26] = onRemovePart;
      $[27] = partKeys;
      $[28] = referencedNfts;
      $[29] = t13;
    } else {
      t13 = $[29];
    }
    t12 = parts.map(t13);
    $[19] = mentionedUsers;
    $[20] = onRemovePart;
    $[21] = partKeys;
    $[22] = parts;
    $[23] = referencedNfts;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  let t13;
  if ($[30] !== t12) {
    t13 = <div className="tw-mt-4 tw-space-y-4"><AnimatePresence mode="popLayout">{t12}</AnimatePresence></div>;
    $[30] = t12;
    $[31] = t13;
  } else {
    t13 = $[31];
  }
  let t14;
  if ($[32] !== t11 || $[33] !== t13) {
    t14 = <div className="tw-flex tw-flex-col tw-w-full">{t11}{t13}</div>;
    $[32] = t11;
    $[33] = t13;
    $[34] = t14;
  } else {
    t14 = $[34];
  }
  let t15;
  if ($[35] !== t14 || $[36] !== t5) {
    t15 = <div className="tw-space-y-4 tw-pb-3"><div className="tw-bg-transparent tw-relative tw-group tw-w-full tw-flex tw-flex-col tw-py-2 tw-transition-colors tw-duration-300"><div className="tw-flex tw-gap-x-3">{t5}{t14}</div></div></div>;
    $[35] = t14;
    $[36] = t5;
    $[37] = t15;
  } else {
    t15 = $[37];
  }
  return t15;
};
export default memo(CreateDropStormParts);